import { Helmet } from 'react-helmet-async';
import { Grid, Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFinanceDashboardOperations } from '../../hooks/useFinanceDashboardOperations';
import FinanceStatsCards from '../../components/finance-dashboard/FinanceStatsCards';
import FinanceCharts from '../../components/finance-dashboard/FinanceCharts';
import FinanceIndicators from '../../components/finance-dashboard/FinanceIndicators';
import FinanceFilters from '../../components/finance-dashboard/FinanceFilters';
import Iconify from '../../components/iconify';

export default function FinanceDashboardPage() {
  const {
    isLoading,
    financialStats,
    monthlyData,
    expenseBreakdown,
    revenueBreakdown,
    cashFlowData,
    recentTransactions,
    budgetData,
    loadDashboardData,
  } = useFinanceDashboardOperations();

  const [searchTerm, setSearchTerm] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });

  const statsCards = [
    {
      title: 'Total Revenue',
      value: `$${(financialStats.totalRevenue / 1000).toFixed(0)}K`,
      icon: <Iconify icon="eva:credit-card-fill" />,
    },
    {
      title: 'Total Expenses',
      value: `$${(financialStats.totalExpenses / 1000).toFixed(0)}K`,
      icon: <Iconify icon="mdi:chart-bar" />,
    },
    {
      title: 'Net Profit',
      value: `$${(financialStats.netProfit / 1000).toFixed(0)}K`,
      icon: <Iconify icon="eva:trending-up-fill" />,
    },
    {
      title: 'Profit Margin',
      value: `${financialStats.profitMargin}%`,
      icon: <Iconify icon="eva:bar-chart-fill" />,
    },
  ];

  useEffect(() => {
    loadDashboardData(dateRange.startDate, dateRange.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'transactionType':
        setTransactionTypeFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    // Data will reload automatically via useEffect watching dateRange
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setTransactionTypeFilter('All');
    setStatusFilter('All');
    setDateRange({
      startDate: null,
      endDate: null,
      preset: 'All',
    });
    // Data will reload automatically via useEffect watching dateRange
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Finance Dashboard</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Finance Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Comprehensive financial overview, performance metrics, and transaction tracking.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <FinanceStatsCards statsCards={statsCards} isLoading={isLoading} />
        </Grid>

        <Grid item xs={12}>
          <FinanceFilters
            searchTerm={searchTerm}
            transactionTypeFilter={transactionTypeFilter}
            statusFilter={statusFilter}
            dateRange={dateRange}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
          />
        </Grid>

        <Grid item xs={12}>
          <FinanceCharts
            monthlyData={monthlyData}
            expenseBreakdown={expenseBreakdown}
            revenueBreakdown={revenueBreakdown}
            cashFlowData={cashFlowData}
            budgetData={budgetData}
            isLoading={isLoading}
          />
        </Grid>

        <Grid item xs={12}>
          <FinanceIndicators recentTransactions={recentTransactions} isLoading={isLoading} />
        </Grid>
      </Grid>
    </>
  );
}
